import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  TrendingUp, Award, DollarSign, Send, ArrowDownToLine, Loader2,
  Clock, CheckCircle2, XCircle, Building2, Users, Wallet, Info,
  Download, Trophy, Rocket, Share2, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { MilestoneProgressRing } from './MilestoneProgressRing';
import { Link } from 'react-router-dom';

const formatPHP = (n) => `₱${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatUSD = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function StockCertificatePage() {
  const { api } = useAuth();
  const [cert, setCert] = useState(null);
  const [history, setHistory] = useState([]);
  const [trades, setTrades] = useState({ outgoing: [], incoming: [] });
  const [pending, setPending] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sellOpen, setSellOpen] = useState(false);
  const [tradeBusy, setTradeBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [certRes, histRes, tradesRes, pendRes, lbRes] = await Promise.all([
        api.get('/api/stocks/my'),
        api.get('/api/stocks/growth-history?limit=60'),
        api.get('/api/stocks/trades'),
        api.get('/api/stocks/trades/pending'),
        api.get('/api/stocks/leaderboard?limit=10'),
      ]);
      setCert(certRes.data.certificate);
      setHistory(histRes.data.history || []);
      setTrades(tradesRes.data || { outgoing: [], incoming: [] });
      setPending(pendRes.data.pending || []);
      setLeaderboard(lbRes.data.leaderboard || []);
    } catch (err) {
      console.error('Failed to load stocks:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const totalUnits = cert?.total_units || 0;
  const hasCert = totalUnits > 0;
  const tierCap = cert?.tier_cap || 0;
  const earnedUnits = cert?.earned_units || 0;
  const progressPct = cert?.progress_pct || 0;
  const eligibility = cert?.eligibility || {};
  const planName = cert?.plan || '';
  const locked = !!cert?.locked;

  const handleAcceptTrade = async (tradeId) => {
    setTradeBusy(true);
    try {
      await api.post(`/api/stocks/trades/${tradeId}/accept`);
      toast.success('Trade accepted — units & wallet updated.');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept trade');
    } finally {
      setTradeBusy(false);
    }
  };

  const handleRejectTrade = async (tradeId) => {
    setTradeBusy(true);
    try {
      await api.post(`/api/stocks/trades/${tradeId}/reject`);
      toast.success('Trade rejected');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setTradeBusy(false);
    }
  };

  const handleCancelTrade = async (tradeId) => {
    setTradeBusy(true);
    try {
      await api.post(`/api/stocks/trades/${tradeId}/cancel`);
      toast.success('Trade cancelled');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setTradeBusy(false);
    }
  };

  if (loading) {
    return (
      <MemberSidebarLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </MemberSidebarLayout>
    );
  }

  return (
    <MemberSidebarLayout>
      <div className="p-6 lg:p-8 space-y-6">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">Stock Certificate</h1>
          <p className="text-[#A0B4CC] mt-1">Your share of All Global Access. Trade with members or sell back to the company.</p>
        </div>

        {locked && (
          <div className="rounded-xl border border-[#FFD740]/30 bg-[#FFD740]/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="stock-locked-banner">
            <p className="text-sm text-[#E6EDF5] flex-1">
              <b className="text-[#FFD740]">Free membership:</b> stock certificates are a Pro/Elite benefit. {hasCert ? 'Your certificate is frozen. You can view it, but trading, selling and downloads stay locked until you upgrade.' : 'Upgrade to start earning your certificate.'}
            </p>
            <Link to="/my-plan" className="rounded-full bg-[#FFD740] text-[#000B18] font-semibold px-4 py-2 text-sm hover:bg-[#FFE57F] transition-colors text-center" data-testid="stock-upgrade-btn">Upgrade to unlock</Link>
          </div>
        )}

        {/* $500-chunk milestone ring — always visible, animates as the
            member's combined subscription value crosses each chunk. */}
        {!locked && <MilestoneProgressRing />}

        {/* Eligibility progress — always visible, shows path to tier cap */}
        {tierCap > 0 && !locked && (
          <>
            <EligibilityProgress
              tierCap={tierCap}
              earnedUnits={earnedUnits}
              progressPct={progressPct}
              eligibility={eligibility}
              planName={planName}
            />
            {earnedUnits < tierCap && (
              <BoostCertCard
                tierCap={tierCap}
                earnedUnits={earnedUnits}
                eligibility={eligibility}
                planName={planName}
              />
            )}
          </>
        )}

        {!hasCert ? (
          <Card className="bg-[#041A3B] border-white/10" data-testid="no-cert-card">
            <CardContent className="py-12 text-center">
              <Award className="w-12 h-12 text-[#5C7599] mx-auto mb-4" />
              <h3 className="font-outfit text-lg text-white mb-2">No certificate yet</h3>
              <p className="text-[#A0B4CC] text-sm max-w-xl mx-auto">
                {tierCap > 0
                  ? <>Grow your wallet and your downline to <span className="text-[#00E5FF] font-semibold">${tierCap}</span> total qualifying value. Your certificate will be issued automatically as you progress.</>
                  : <>Activate a Pro or Elite membership plan to start earning your stock certificate.</>}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Hero value cards */}
            <div className="grid md:grid-cols-3 gap-4">
              <ValueCard
                icon={Award} color="#00E5FF"
                label="Units Owned" value={totalUnits.toLocaleString()}
                sub={`Base ${cert.base_units} • Bought ${cert.units_bought || 0} • Sold ${cert.units_sold || 0}`}
                testId="stock-units-card"
              />
              <ValueCard
                icon={DollarSign} color="#00E676"
                label="Current Value (USD)" value={formatUSD(cert.current_value_usd)}
                sub={`1 unit = ${formatUSD(cert.unit_usd)} • Growth × ${cert.growth_multiplier.toFixed(2)}`}
                testId="stock-value-usd-card"
              />
              <ValueCard
                icon={TrendingUp} color="#FFB300"
                label="Current Value (PHP)" value={formatPHP(cert.subscription_value_php ?? cert.current_value_php)}
                sub={`Subscription plan price · ${cert.subscription_value_php != null ? 'fixed to plan tier' : `FX ${(1 / (cert.php_usd_rate || 1)).toFixed(2)} PHP/USD`}`}
                testId="stock-value-php-card"
              />
            </div>

            {/* Action: Download + Sell */}
            <div className="flex justify-end gap-2">
              <Button
                onClick={async () => {
                  try {
                    const res = await api.get('/api/stocks/certificate-pdf', { responseType: 'blob' });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'AGA-Stock-Certificate.pdf';
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    toast.error('Failed to download certificate');
                  }
                }}
                variant="outline"
                disabled={locked}
                className="border-white/20 text-white hover:bg-white/5"
                data-testid="download-certificate-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Certificate PDF
              </Button>
              <Button onClick={() => setSellOpen(true)} disabled={locked} className="btn-primary" data-testid="open-sell-dialog">
                <Send className="w-4 h-4 mr-2" /> Sell Stock
              </Button>
            </div>

            {/* Pending incoming trades */}
            {pending.length > 0 && (
              <Card className="bg-[#041A3B] border-[#FFB300]/30">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#FFB300]" />
                    Incoming Trade Offers ({pending.length})
                  </CardTitle>
                  <CardDescription className="text-[#A0B4CC]">
                    Another member wants to sell you stock. Accept = wallet is debited, units credited.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {pending.map((t) => (
                    <div key={t.id} className="rounded-lg border border-white/10 bg-[#000B18] p-4 flex flex-wrap items-center gap-4" data-testid={`pending-trade-${t.id}`}>
                      <div className="flex-1 min-w-[200px]">
                        <p className="text-white font-medium">{t.units} units</p>
                        <p className="text-[#A0B4CC] text-xs">
                          Total {formatPHP(t.total_php)} ({formatUSD(t.total_usd)}) — expires {new Date(t.expires_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" disabled={tradeBusy || locked} onClick={() => handleAcceptTrade(t.id)} className="bg-[#00E676] hover:bg-[#00E676]/90 text-[#000B18]" data-testid={`accept-trade-${t.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Accept
                        </Button>
                        <Button size="sm" variant="ghost" disabled={tradeBusy} onClick={() => handleRejectTrade(t.id)} className="text-[#FF1744]" data-testid={`reject-trade-${t.id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Growth chart */}
            <Card className="bg-[#041A3B] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Unit Value Growth</CardTitle>
                <CardDescription className="text-[#A0B4CC]">
                  Historical unit value in USD. Admin-controlled growth multiplier × FX rate.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {history.length < 2 ? (
                  <div className="py-12 text-center text-[#5C7599] text-sm">
                    <Info className="w-6 h-6 mx-auto mb-2" />
                    Growth history will appear here as the admin updates unit value over time.
                  </div>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={history}>
                        <defs>
                          <linearGradient id="stockGrowth" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.4} />
                            <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="date" stroke="#5C7599" fontSize={10} />
                        <YAxis stroke="#5C7599" fontSize={10} domain={['auto', 'auto']} />
                        <Tooltip
                          contentStyle={{ background: '#000B18', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                          labelStyle={{ color: '#A0B4CC' }}
                          formatter={(v) => formatUSD(v)}
                        />
                        <Area type="monotone" dataKey="unit_usd" stroke="#00E5FF" fill="url(#stockGrowth)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Leaderboard */}
            {leaderboard.length > 0 && (
              <Card className="bg-[#041A3B] border-white/10" data-testid="stock-leaderboard">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#FFB300]" />
                    Top Stockholders
                  </CardTitle>
                  <CardDescription className="text-[#A0B4CC]">
                    Anonymized leaderboard of the largest AGA stock-cert holders.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {leaderboard.slice(0, 10).map((row) => (
                      <div key={row.rank} className="flex items-center gap-3 p-2 rounded bg-[#000B18]/60">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                          row.rank === 1 ? 'bg-[#FFB300]/20 text-[#FFB300]' :
                          row.rank === 2 ? 'bg-[#C0C0C0]/20 text-[#E0E0E0]' :
                          row.rank === 3 ? 'bg-[#CD7F32]/20 text-[#CD7F32]' :
                          'bg-white/5 text-[#A0B4CC]'
                        }`}>{row.rank}</div>
                        <p className="text-white text-sm flex-1">{row.display_name}</p>
                        <p className="text-[#A0B4CC] text-sm">{row.units.toLocaleString()} units</p>
                        <p className="text-[#00E676] text-sm font-medium">{formatUSD(row.value_usd)}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Trade history */}
            <Card className="bg-[#041A3B] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Trade History</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="outgoing">
                  <TabsList className="bg-[#000B18] mb-4">
                    <TabsTrigger value="outgoing" className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-[#000B18]">
                      Sold by me ({trades.outgoing.length})
                    </TabsTrigger>
                    <TabsTrigger value="incoming" className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-[#000B18]">
                      Bought by me ({trades.incoming.length})
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="outgoing">
                    <TradeList trades={trades.outgoing} role="seller" onCancel={handleCancelTrade} tradeBusy={tradeBusy} />
                  </TabsContent>
                  <TabsContent value="incoming">
                    <TradeList trades={trades.incoming} role="buyer" />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </>
        )}

        <SellDialog
          open={sellOpen}
          onOpenChange={setSellOpen}
          api={api}
          cert={cert}
          onSuccess={() => { setSellOpen(false); refresh(); }}
        />
      </div>
    </MemberSidebarLayout>
  );
}

function ValueCard({ icon: Icon, color, label, value, sub, testId }) {
  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}1A` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
        <p className="text-[#A0B4CC] text-xs uppercase tracking-wider mb-1">{label}</p>
        <p className="font-outfit text-2xl font-bold text-white">{value}</p>
        {sub && <p className="text-[#5C7599] text-xs mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function TradeList({ trades, role, onCancel, tradeBusy }) {
  if (trades.length === 0) {
    return <p className="text-[#5C7599] text-sm text-center py-6">No trades yet.</p>;
  }
  return (
    <div className="space-y-2">
      {trades.map((t) => (
        <div key={t.id} className="rounded-lg border border-white/10 bg-[#000B18] p-3 flex items-center gap-3 flex-wrap" data-testid={`trade-row-${t.id}`}>
          <Badge className={
            t.trade_type === 'buyback' ? 'bg-[#007AFF]/20 text-[#007AFF]' : 'bg-[#00E5FF]/20 text-[#00E5FF]'
          }>
            {t.trade_type === 'buyback' ? 'Buyback' : 'P2P'}
          </Badge>
          <StatusBadge status={t.status} />
          <div className="flex-1 min-w-[160px]">
            <p className="text-white text-sm">{t.units} units</p>
            <p className="text-[#5C7599] text-xs">
              {new Date(t.created_at).toLocaleString()} • {formatPHP(t.total_php)} ({formatUSD(t.total_usd)})
            </p>
          </div>
          {role === 'seller' && t.status === 'pending' && (
            <Button size="sm" variant="ghost" disabled={tradeBusy} onClick={() => onCancel(t.id)} className="text-[#FF1744]">
              Cancel
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: 'bg-[#FFB300]/20 text-[#FFB300]',
    completed: 'bg-[#00E676]/20 text-[#00E676]',
    rejected: 'bg-[#FF1744]/20 text-[#FF1744]',
    expired: 'bg-[#5C7599]/30 text-[#A0B4CC]',
    cancelled: 'bg-[#5C7599]/30 text-[#A0B4CC]',
  };
  return <Badge className={`${map[status] || 'bg-white/10 text-white'} text-[10px] uppercase`}>{status}</Badge>;
}

function SellDialog({ open, onOpenChange, api, cert, onSuccess }) {
  const [mode, setMode] = useState('member'); // member | company
  const [recipientEmail, setRecipientEmail] = useState('');
  const [units, setUnits] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setMode('member');
      setRecipientEmail('');
      setUnits('');
    }
  }, [open]);

  const u = parseInt(units, 10) || 0;
  const totalPhp = u * (cert?.unit_php || 0);
  const totalUsd = u * (cert?.unit_usd || 0);

  const submit = async () => {
    if (u < 10) { toast.error('Minimum trade is 10 units.'); return; }
    if (u > (cert?.total_units || 0)) { toast.error('You do not own that many units.'); return; }
    setBusy(true);
    try {
      if (mode === 'member') {
        if (!recipientEmail) { toast.error('Enter recipient email.'); setBusy(false); return; }
        await api.post('/api/stocks/sell-to-member', { recipient_email: recipientEmail, units: u });
        toast.success('Trade sent — waiting for buyer to confirm.');
      } else {
        await api.post('/api/stocks/sell-to-company', { units: u });
        toast.success(`Buyback complete — ${formatPHP(totalPhp)} credited to your wallet.`);
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate trade');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">Sell Stock</DialogTitle>
          <DialogDescription className="text-[#A0B4CC]">
            Min 10 units per trade • 24h cooldown • Live FX rate at sale time
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Mode selector */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setMode('member')}
              className={`p-3 rounded-lg border flex flex-col items-center gap-1 transition-colors ${mode === 'member' ? 'border-[#00E5FF] bg-[#00E5FF]/10' : 'border-white/10 bg-[#000B18] hover:border-white/30'}`}
              data-testid="sell-mode-member"
            >
              <Users className="w-5 h-5 text-[#00E5FF]" />
              <span className="text-white text-sm font-medium">To another member</span>
              <span className="text-[#5C7599] text-[10px] text-center">Pending until buyer accepts</span>
            </button>
            <button
              type="button"
              onClick={() => setMode('company')}
              className={`p-3 rounded-lg border flex flex-col items-center gap-1 transition-colors ${mode === 'company' ? 'border-[#00E5FF] bg-[#00E5FF]/10' : 'border-white/10 bg-[#000B18] hover:border-white/30'}`}
              data-testid="sell-mode-company"
            >
              <Building2 className="w-5 h-5 text-[#00E676]" />
              <span className="text-white text-sm font-medium">Sell to AGA (Buyback)</span>
              <span className="text-[#5C7599] text-[10px] text-center">Instant — credits wallet</span>
            </button>
          </div>

          {mode === 'member' && (
            <div className="space-y-2">
              <Label className="text-white">Recipient member email</Label>
              <Input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="buyer@example.com"
                className="bg-[#000B18] border-white/20 text-white"
                data-testid="recipient-email-input"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-white">Units to sell ({(cert?.total_units || 0).toLocaleString()} available)</Label>
            <Input
              type="number"
              min={10}
              max={cert?.total_units || 0}
              value={units}
              onChange={(e) => setUnits(e.target.value)}
              placeholder="e.g., 50"
              className="bg-[#000B18] border-white/20 text-white"
              data-testid="units-input"
            />
          </div>

          {/* Summary */}
          <div className="rounded-lg bg-[#000B18] border border-white/10 p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#A0B4CC]">Per-unit value</span>
              <span className="text-white">{formatPHP(cert?.unit_php || 0)} / {formatUSD(cert?.unit_usd || 0)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#A0B4CC]">Units</span>
              <span className="text-white">{u.toLocaleString()}</span>
            </div>
            <div className="flex justify-between border-t border-white/10 pt-2">
              <span className="text-white font-medium">You&apos;ll receive</span>
              <div className="text-right">
                <p className="text-[#00E676] font-bold">{formatPHP(totalPhp)}</p>
                <p className="text-[#5C7599] text-xs">{formatUSD(totalUsd)}</p>
              </div>
            </div>
            <p className="text-[#5C7599] text-[11px] flex items-start gap-1">
              <Wallet className="w-3 h-3 mt-0.5 shrink-0" />
              {mode === 'company'
                ? 'Buyback credits your withdrawable wallet immediately at the current FX rate.'
                : 'Buyer must accept within 7 days. On accept, their wallet is debited and yours is credited.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Cancel</Button>
          <Button onClick={submit} disabled={busy || u < 10} className="btn-primary" data-testid="confirm-sell-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'company' ? <ArrowDownToLine className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />)}
            {mode === 'company' ? 'Sell to AGA' : 'Send Trade Offer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Eligibility Progress Card ──────────────────────────────────────────────
// Shows the member's progress toward their tier's cert cap with a breakdown
// of wallet + L1 + L2 referral USD value. Rendered above the cert section so
// members always understand how to earn / grow their certificate.
function EligibilityProgress({ tierCap, earnedUnits, progressPct, eligibility, planName }) {
  const e = eligibility || {};
  const bar = Math.max(0, Math.min(100, progressPct || 0));
  const reached = (earnedUnits || 0) >= tierCap && tierCap > 0;

  return (
    <Card className="bg-gradient-to-br from-[#041A3B] to-[#0A254D] border-[#00E5FF]/30" data-testid="eligibility-progress-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="w-5 h-5 text-[#00E5FF]" />
              <h3 className="font-outfit text-lg sm:text-xl text-white">Stock Certificate Progress</h3>
            </div>
            <p className="text-[#A0B4CC] text-sm">
              {planName ? <>Your <span className="text-white font-medium">{planName}</span> tier unlocks a maximum </> : 'Maximum cert face value '}
              <span className="text-[#00E5FF] font-semibold">${tierCap.toLocaleString()}</span> stock certificate.
            </p>
          </div>
          <Badge className={reached ? 'bg-[#00E676]/20 text-[#00E676] border-0' : 'bg-[#00E5FF]/15 text-[#00E5FF] border-0'} data-testid="eligibility-status-badge">
            {reached ? 'Goal reached' : `${bar.toFixed(1)}% to goal`}
          </Badge>
        </div>

        {/* Progress bar */}
        <div className="space-y-2 mb-5">
          <div className="flex justify-between text-xs text-[#A0B4CC]">
            <span data-testid="eligibility-current-usd">${(e.total_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} earned</span>
            <span>${tierCap.toLocaleString()} goal</span>
          </div>
          <div className="h-3 rounded-full bg-[#000B18] border border-white/10 overflow-hidden" data-testid="eligibility-progress-bar">
            <div
              className={`h-full transition-all duration-700 ${reached ? 'bg-gradient-to-r from-[#00E676] to-[#00E5FF]' : 'bg-gradient-to-r from-[#00E5FF] to-[#007AFF]'}`}
              style={{ width: `${bar}%` }}
              data-testid="eligibility-progress-fill"
            />
          </div>
        </div>

        {/* Breakdown grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <BreakdownTile
            icon={Wallet}
            label="Your Plan"
            php={e.self_php ?? e.wallet_php}
            usd={e.self_usd ?? e.wallet_usd}
            testId="breakdown-self"
          />
          <BreakdownTile
            icon={Users}
            label={`Level 1 (${e.l1_count || 0})`}
            php={e.l1_php}
            usd={e.l1_usd}
            testId="breakdown-l1"
          />
          <BreakdownTile
            icon={Users}
            label={`Level 2 (${e.l2_count || 0})`}
            php={e.l2_php}
            usd={e.l2_usd}
            testId="breakdown-l2"
          />
        </div>

        <p className="text-[#5C7599] text-[11px] mt-3 flex items-start gap-1">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          PHP → USD at live rate {e.php_usd_rate ? `(₱${(1 / e.php_usd_rate).toFixed(2)} = $1)` : ''}. As your downline subscribes and your wallet grows, your certificate is issued/updated automatically up to the ${tierCap} tier cap.
        </p>
      </CardContent>
    </Card>
  );
}

function BreakdownTile({ icon: Icon, label, php, usd, testId }) {
  return (
    <div className="rounded-lg bg-[#000B18] border border-white/10 p-3" data-testid={testId}>
      <div className="flex items-center gap-2 text-[#A0B4CC] text-xs mb-1">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <p className="text-white font-semibold text-base leading-tight">
        ${(usd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </p>
      <p className="text-[#5C7599] text-[11px]">
        ₱{(php || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </p>
    </div>
  );
}

// ─── "Boost Your Cert" CTA ────────────────────────────────────────────────
// Calculates how many more L1 referrals (at the user's own plan price) it
// takes to close the gap to their tier cap and renders a referral-share
// call-to-action. Hidden once goal is reached.
function BoostCertCard({ tierCap, earnedUnits, eligibility, planName }) {
  const { user } = useAuth();
  const [referralLink, setReferralLink] = useState('');

  useEffect(() => {
    const code = user?.referral_code;
    if (code) {
      setReferralLink(`${window.location.origin}/register?ref=${code}`);
    }
  }, [user]);

  const e = eligibility || {};
  const remainingUsd = Math.max(0, tierCap - earnedUnits);
  const planPriceUsd = e.self_usd ?? e.wallet_usd ?? 0;
  // Avoid divide-by-zero — if we don't know the plan price, fall back to a
  // sensible $74 (Basic ₱4,500 ≈ $74) so we still show actionable numbers.
  const refPriceUsd = planPriceUsd > 1 ? planPriceUsd : 74;
  const refsNeeded = Math.ceil(remainingUsd / refPriceUsd);

  const onCopy = async () => {
    if (!referralLink) {
      toast.error('Your referral code is loading — try again in a sec.');
      return;
    }
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success('Referral link copied to clipboard!');
    } catch {
      toast.error('Could not copy — long-press the link to copy manually.');
    }
  };

  const onShare = async () => {
    if (!referralLink) return;
    const shareData = {
      title: 'Join me on All Global Access',
      text: `I'm building my $${tierCap} stock certificate on AGA — join me using my referral link and we both win.`,
      url: referralLink,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        onCopy();
      }
    } catch { /* user dismissed */ }
  };

  return (
    <Card className="keep-dark bg-gradient-to-br from-[#0A254D] to-[#041A3B] border-[#00E676]/30" data-testid="boost-cert-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="rounded-full bg-[#00E676]/15 p-2 shrink-0">
            <Rocket className="w-5 h-5 text-[#00E676]" />
          </div>
          <div className="flex-1">
            <h3 className="font-outfit text-lg sm:text-xl text-white">Boost your stock certificate</h3>
            <p className="text-[#A0B4CC] text-sm mt-1">
              You&apos;re <span className="text-[#00E5FF] font-semibold">${remainingUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> USD away from your full <span className="text-white">${tierCap.toLocaleString()}</span> {planName || ''} certificate.
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-[#000B18] border border-[#00E676]/20 p-4 mb-4" data-testid="boost-cert-suggestion">
          <p className="text-white text-sm">
            🎯 Refer <span className="text-[#00E676] font-bold text-base">{refsNeeded}</span> more {refsNeeded === 1 ? 'member' : 'members'} at your plan tier to unlock your full ${tierCap.toLocaleString()} stock certificate.
          </p>
          <p className="text-[#5C7599] text-xs mt-1">
            Each {planName || 'qualifying'} referral adds ~${refPriceUsd.toFixed(0)} USD to your qualifying value.
          </p>
        </div>

        {referralLink && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <Input
                value={referralLink}
                readOnly
                className="bg-[#000B18] border-white/10 text-white text-xs sm:text-sm font-mono"
                data-testid="boost-referral-link-input"
                onClick={(e) => e.target.select()}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={onCopy}
                className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90 flex-1 sm:flex-none"
                data-testid="boost-copy-btn"
              >
                <Copy className="w-4 h-4 mr-2" /> Copy link
              </Button>
              <Button
                onClick={onShare}
                variant="outline"
                className="border-[#00E676]/50 text-[#00E676] hover:bg-[#00E676]/10 flex-1 sm:flex-none"
                data-testid="boost-share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default StockCertificatePage;
